/**
 * Единая палитра цветов валют для кодов, бейджей и карточек.
 * Используется во всех местах отображения валют (главная, сделки, курсы, карточка клиента).
 */

/** Цвета по коду валюты: текст, бейдж, фон карточки (light/dark) */
const CURRENCY_PALETTE = {
  RUB: {
    text: 'text-blue-600',
    textDark: 'text-blue-300',
    badge: 'bg-blue-100 text-blue-800',
    badgeDark: 'bg-blue-900/30 text-blue-300 border border-blue-500/40',
    cardBg: 'bg-blue-50',
    cardBgDark: 'bg-blue-900/20',
    cardBorder: 'border-blue-200',
    cardBorderDark: 'border-blue-500/40',
  },
  USD: {
    text: 'text-green-600',
    textDark: 'text-green-300',
    badge: 'bg-green-100 text-green-800',
    badgeDark: 'bg-green-900/30 text-green-300 border border-green-500/40',
    cardBg: 'bg-green-50',
    cardBgDark: 'bg-green-900/20',
    cardBorder: 'border-green-200',
    cardBorderDark: 'border-green-500/40',
  },
  USDT: {
    text: 'text-green-600',
    textDark: 'text-green-300',
    badge: 'bg-green-100 text-green-800',
    badgeDark: 'bg-green-900/30 text-green-300 border border-green-500/40',
    cardBg: 'bg-green-50',
    cardBgDark: 'bg-green-900/20',
    cardBorder: 'border-green-200',
    cardBorderDark: 'border-green-500/40',
  },
  EUR: {
    text: 'text-purple-600',
    textDark: 'text-purple-300',
    badge: 'bg-purple-100 text-purple-800',
    badgeDark: 'bg-purple-900/30 text-purple-300 border border-purple-500/40',
    cardBg: 'bg-purple-50',
    cardBgDark: 'bg-purple-900/20',
    cardBorder: 'border-purple-200',
    cardBorderDark: 'border-purple-500/40',
  },
  THB: {
    text: 'text-orange-600',
    textDark: 'text-orange-300',
    badge: 'bg-orange-100 text-orange-800',
    badgeDark: 'bg-orange-900/30 text-orange-300 border border-orange-500/40',
    cardBg: 'bg-orange-50',
    cardBgDark: 'bg-orange-900/20',
    cardBorder: 'border-orange-200',
    cardBorderDark: 'border-orange-500/40',
  },
  CNY: {
    text: 'text-red-600',
    textDark: 'text-red-300',
    badge: 'bg-red-100 text-red-800',
    badgeDark: 'bg-red-900/30 text-red-300 border border-red-500/40',
    cardBg: 'bg-red-50',
    cardBgDark: 'bg-red-900/20',
    cardBorder: 'border-red-200',
    cardBorderDark: 'border-red-500/40',
  },
  SGD: {
    text: 'text-amber-600',
    textDark: 'text-amber-300',
    badge: 'bg-amber-100 text-amber-800',
    badgeDark: 'bg-amber-900/30 text-amber-300 border border-amber-500/40',
    cardBg: 'bg-amber-50',
    cardBgDark: 'bg-amber-900/20',
    cardBorder: 'border-amber-200',
    cardBorderDark: 'border-amber-500/40',
  },
};

const DEFAULT = {
  text: 'text-gray-700',
  textDark: 'text-gray-300',
  badge: 'bg-gray-100 text-gray-800',
  badgeDark: 'bg-gray-700 text-gray-300 border border-gray-600',
  cardBg: 'bg-gray-50',
  cardBgDark: 'bg-gray-800/50',
  cardBorder: 'border-gray-200',
  cardBorderDark: 'border-gray-600',
};

const NORMALIZE = { '₽': 'RUB', '€': 'EUR', '¥': 'CNY', '฿': 'THB', '$': 'USD' };

/**
 * Нормализует код валюты для поиска в палитре (RUB, EUR, USD, ...).
 * @param {string} currency - Код или символ валюты
 * @returns {string} Ключ в CURRENCY_PALETTE или пустая строка
 */
function normalizeCurrency(currency) {
  if (!currency) return '';
  const u = (currency + '').trim().toUpperCase();
  if (NORMALIZE[u]) return NORMALIZE[u];
  if (CURRENCY_PALETTE[u]) return u;
  // "RUB CASH" -> "RUB"
  const first = u.split(/\s+/)[0];
  return CURRENCY_PALETTE[first] ? first : u;
}

/**
 * Класс текста для кода валюты (везде, где показывается RUB/EUR/USD и т.д.).
 * @param {string} currency - Код валюты
 * @param {boolean} isDark - Тёмная тема
 * @returns {string} Tailwind-классы для текста
 */
export function getCurrencyTextClass(currency, isDark = false) {
  const key = normalizeCurrency(currency);
  const pal = (key && CURRENCY_PALETTE[key]) ? CURRENCY_PALETTE[key] : DEFAULT;
  return isDark ? pal.textDark : pal.text;
}

/**
 * Классы для бейджа валюты (таблицы, карточки сделок).
 * @param {string} currency - Код валюты
 * @param {boolean} isDark - Тёмная тема
 * @returns {string} Tailwind-классы для бейджа
 */
export function getCurrencyBadgeClass(currency, isDark = false) {
  const key = normalizeCurrency(currency);
  const pal = (key && CURRENCY_PALETTE[key]) ? CURRENCY_PALETTE[key] : DEFAULT;
  return isDark ? pal.badgeDark : pal.badge;
}

/**
 * Классы фона и границы карточки валюты на вкладке «Курсы валюты».
 * @param {string} currency - Код валюты
 * @param {boolean} isDark - Тёмная тема
 * @returns {{ cardBg: string, cardBorder: string }}
 */
export function getCurrencyCardClasses(currency, isDark = false) {
  const key = normalizeCurrency(currency);
  const pal = (key && CURRENCY_PALETTE[key]) ? CURRENCY_PALETTE[key] : DEFAULT;
  return {
    cardBg: isDark ? pal.cardBgDark : pal.cardBg,
    cardBorder: isDark ? pal.cardBorderDark : pal.cardBorder,
  };
}
